/**
 * Shared, modern PDF upload control used by all three template dialogs.
 * Presentational only — the parent owns the upload/state logic and passes
 * callbacks. A dashed dropzone card is the primary action; a secondary muted
 * input keeps the "paste a URL" capability.
 */
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileCheck2, Loader2, UploadCloud } from "lucide-react";

export function PdfUploadField({
  value,
  onValueChange,
  onFile,
  uploading,
  loadingExisting,
  fileName,
}: {
  value: string;
  onValueChange: (v: string) => void;
  onFile: (file: File) => void;
  uploading: boolean;
  /** URL present but the bytes haven't finished loading into the preview yet. */
  loadingExisting?: boolean;
  /** Optional filename to show once a PDF is attached. */
  fileName?: string;
}) {
  const hasPdf = !!value;

  function pick() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) onFile(file);
    };
    input.click();
  }

  return (
    <div className="space-y-2">
      <Label>Template PDF *</Label>
      <button
        type="button"
        onClick={pick}
        disabled={uploading}
        className="group flex w-full flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed bg-muted/20 px-4 py-6 text-center transition-colors hover:border-primary/50 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
          {uploading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : hasPdf ? (
            <FileCheck2 className="h-5 w-5" />
          ) : (
            <UploadCloud className="h-5 w-5" />
          )}
        </span>
        <span className="text-sm font-medium">
          {uploading
            ? "Uploading…"
            : hasPdf
              ? "PDF attached — click to replace"
              : "Click to upload a PDF"}
        </span>
        {hasPdf && fileName && (
          <span className="max-w-full truncate font-mono text-[11px] text-muted-foreground">
            {fileName}
          </span>
        )}
        {loadingExisting && (
          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading preview…
          </span>
        )}
      </button>
      <Input
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        placeholder="…or paste a PDF URL"
        className="text-xs text-muted-foreground"
      />
    </div>
  );
}
